/**
 * The player's access code for the sheets API, remembered per Owlbear room in
 * this browser. One code per campaign room; the GM issues it from the Obsidian
 * GM Console. The server is the authority on whether a code is valid.
 */
const PREFIX = 'com.lordofdisndat.dcc/player-code/v1/';

export function playerCodeStorageKey(roomId: string): string {
  return `${PREFIX}${roomId}`;
}

export function readPlayerCode(roomId: string): string {
  try { return globalThis.localStorage?.getItem(playerCodeStorageKey(roomId))?.trim() ?? ''; }
  catch { return ''; }
}

export function writePlayerCode(roomId: string, code: string): boolean {
  try { globalThis.localStorage?.setItem(playerCodeStorageKey(roomId), code.trim()); return true; }
  catch { return false; }
}

export function clearPlayerCode(roomId: string): boolean {
  try { globalThis.localStorage?.removeItem(playerCodeStorageKey(roomId)); return true; }
  catch { return false; }
}

export type WhoAmI =
  | { ok: true; playerId: string; label: string }
  | { ok: false; reason: 'invalid' | 'other-room' | 'offline' | 'server'; message: string };

/** Asks the sheets API who this code belongs to, in this room. */
export async function whoAmI(code: string, roomId: string): Promise<WhoAmI> {
  let response: Response;
  try {
    response = await fetch('/api/sheets', {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${code.trim()}` },
      body: JSON.stringify({ action: 'player.whoami', roomId }),
      cache: 'no-store',
    });
  } catch {
    return { ok: false, reason: 'offline', message: 'Could not reach the sheet server. Check your connection and try again.' };
  }
  let body: { ok?: boolean; error?: string; player?: { playerId?: unknown; label?: unknown } } = {};
  try { body = await response.json() as typeof body; } catch { /* handled below */ }

  if (response.status === 401) {
    return { ok: false, reason: 'invalid', message: 'That code is not valid, or your GM has replaced it. Ask your GM for your current code.' };
  }
  if (response.status === 403) {
    return { ok: false, reason: 'other-room', message: 'That code belongs to a different campaign room.' };
  }
  const player = body.player;
  if (response.ok && body.ok === true && typeof player?.playerId === 'string' && typeof player.label === 'string') {
    return { ok: true, playerId: player.playerId, label: player.label };
  }
  return { ok: false, reason: 'server', message: body.error ?? `The sheet server returned an error (${response.status}).` };
}
